<template>
  <div class="spnod-admin__form--container container pl-0 mx-0">
    <b-form @submit.stop.prevent="handleSubmit">
      <b-form-group class="mb-2">
        <select
          :selected="selectedPlanName"
          v-model="$v.selectedPlan.$model"
          :state="validateState(['selectedPlan'])"
          class="form-control"
          size="sm"
        >
          <option :value="null">Select a plan</option>
          <option
            v-for="plan in planList"
            :key="plan.index"
            :name="plan.nickname"
            :value="plan">
            {{ `${plan.nickname}: $${plan.amount / 100}` }}
          </option>
        </select>
      </b-form-group>
      <b-form-input
        name="name"
        v-model="$v.form.name.$model"
        :state="validateState(['form', 'name'])"
        type="text"
        placeholder="Name"
        size="sm"
        class="mb-2"
      ></b-form-input>
      <b-form-input
        name="email"
        v-model="$v.form.email.$model"
        :state="validateState(['form', 'email'])"
        type="text"
        placeholder="Email"
        size="sm"
        class="mb-2"
        :disabled="true"
      ></b-form-input>
      <b-form-input
        name="address_line1"
        v-model="$v.form.address_line1.$model"
        :state="validateState(['form', 'address_line1'])"
        type="text"
        placeholder="Address (street)"
        size="sm"
        class="mb-2"
      ></b-form-input>
      <b-form-input
        name="address_line2"
        v-model="$v.form.address_line2.$model"
        :state="validateState(['form', 'address_line2'])"
        type="text"
        placeholder="Address (continued)"
        size="sm"
        class="mb-2"
      ></b-form-input>
      <b-form-input
        name="city"
        v-model="$v.form.city.$model"
        :state="validateState(['form', 'city'])"
        type="text"
        placeholder="City"
        size="sm"
        class="mb-2"
      ></b-form-input>
      <b-form-row class="mb-2">
        <b-col>
          <b-form-group class="mb-0" size="sm">
            <select
              name="state"
              v-model="$v.form.state.$model"
              :state="validateState(['form', 'state'])"
              class="form-control py-1"
              size="sm"
            >
              <option v-for="(state, index) in states" :key="index" :value="state">
                {{ state }}
              </option>
            </select>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-input
            name="zip"
            v-model="$v.form.zip.$model"
            :state="validateState(['form', 'zip'])"
            type="text"
            placeholder="Zipcode"
            size="sm"
          ></b-form-input>
        </b-col>
      </b-form-row>
      <b-form-input
        v-model="$v.form.couponCode.$model"
        :state="validateState(['form', 'couponCode'])"
        @change="validateCoupon($event)"
        type="text"
        placeholder="Coupon (optional)"
        size="sm"
        class="mb-2"
      ></b-form-input>
      <Card :stripe='getStripeKey' @change="checkCard($event)" class="border px-2 py-1 mb-2"/>
      <p class="text-danger mb-2" v-if="activeSub">
        You will be charged a prorated amount for this user(s) for the remainder of your current billing cycle.
        They will then be added in to your next group payment.
      </p>
      <b-form-row class="px-1 mb-4 d-flex justify-content-between align-items-center">
        <p>Total: ${{this.firstPayment}}</p>
        <b-form-group>
           <b-button
            class="spnod-admin__form--button"
            type="button"
            size="sm"
            @click="$router.go(-1)">
            Back
          </b-button>
          <b-button
            class="spnod-admin__form--button"
            type="button"
            size="sm"
            @click="handleSkip">
            Skip
          </b-button>
          <b-button class="spnod-admin__form--button" type="submit" size="sm" :disabled="isDisabled">
            <p v-if="!isSending">Submit</p>
            <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          </b-button>
        </b-form-group>
      </b-form-row>
    </b-form>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters, mapActions } from 'vuex'
import { required, numeric, email, helpers } from 'vuelidate/lib/validators'
import { validationMixin } from 'vuelidate'
import { createPaymentMethod, Card } from 'vue-stripe-elements-plus'

export default {
  name: 'PaymentForm',
  mixin: validationMixin,
  props: ['forClient', 'bulk', 'activeSub', 'groupRedirect'],
  data() {
    return {
      form: {
        name: null,
        email: null,
        phone: null,
        address_line1: null,
        address_line2: null,
        city: null,
        state: null,
        zip: null,
        couponCode: null
      },
      planList: [],
      states: [],
      selectedPlan: null,
      coupon: null,
      complete: false,
      isSending: false
    }
  },
  components: { Card },
  computed: {
    ...mapGetters([
      'getPlans',
      'getMyInfo',
      'getStripeKey',
      'users',
      'activeGroup'
    ]),
    selectedPlanName() {
      if (this.selectedPlan) {
        return this.selectedPlan.nickname
      }
    },
    selectedPrice() {
      let price = null
      if (this.selectedPlan) {
        price = this.selectedPlan.amount / 100
        if (this.bulk) {
          const userCount = this.users.data.length
          price = price * userCount
        }
      }
      return price
    },
    selectedRange() {
      let range = 'monthly'
      if (this.selectedPlan) {
        const transform = this.selectedPlan.transform_usage
        if (transform) {
          range = `every ${transform.divide_by} months`
        }
      }
      return range
    },
    firstPayment() {
      return (this.selectedPrice - this.discount).toFixed(2)
    },
    discount() {
      let amount = 0
      if (this.coupon) {
        if (this.coupon.amount_off) {
          amount = this.coupon.amount_off / 100
          if (this.bulk) {
            amount = amount * this.users.data.length
          }
        } else {
          amount = this.selectedPrice * (this.coupon.percent_off / 100)
        }
      }
      return amount
    },
    anyBlanks() {
      return Object.entries(this.form).some((arr) => {
        return arr[0] !== 'address_line2' && arr[0] !== 'couponCode' && (arr[1] === null || arr[1] === '')
      })
    },
    isDisabled() {
      return this.anyBlanks || this.$v.form.$anyError || !this.complete
    },
    biometricsIncomplete() {
      let surveyIncomplete = true

      if (this.getMyInfo.surveys.length > 0) {
        surveyIncomplete = this.getMyInfo.surveys[0].questionsGroups[0].questions.every(el => !el.answer || el.answer === '')
      }

      return surveyIncomplete || !this.getMyInfo.biometrics
    },
    isMobile() { return window.innerWidth < 1200 }
  },
  methods: {
    ...mapActions([
      'getMe',
      'loadPlans',
      'getStates',
      'loadCoupon',
      'subscribe',
      'subscribeClient',
      'subscribeBulkClients',
      'resetNewUser',
      'resetUsers',
      'loadSubscriptions'
    ]),
    validateState(props) {
      const { $dirty, $error } = props.reduce((a, prop) => a[prop], this.$v)
      return $dirty ? !$error : null
    },
    async pay() {
      this.isSending = true
      this.orderErrors = []
      try {
        const { paymentMethod } = await createPaymentMethod('card', {
          billing_details: {
            name: this.form.name,
            email: this.form.email,
            phone: this.form.phone,
            address: {
              line1: this.form.address_line1,
              line2: this.form.address_line2,
              city: this.form.city.toLowerCase(),
              state: this.form.state,
              postal_code: this.form.zip
            }
          }
        })
        if (paymentMethod) {
          let action
          if (this.forClient && this.bulk) {
            action = this.subscribeBulkClients
          } else if (this.forClient) {
            action = this.subscribeClient
          } else {
            action = this.subscribe
          }
          const subscription = await action({
            couponCode: this.coupon ? this.coupon.id : null,
            plan: this.selectedPlan.id,
            paymentMethod: paymentMethod.id,
            customer: this.form
          })
          if (!this.forClient) this.loadSubscriptions()
          if (subscription) {
            switch (subscription.status) {
              case 'active':
                this.resetNewUser()
                this.resetUsers()
                if (!this.forClient && this.biometricsIncomplete) {
                  this.showBiometrics()
                } else {
                  this.showComplete()
                }
                break
              case 'incomplete':
                // check subscription.latest_invoice.payment_intent.status for requires_action or processing
                // if requires_action, we may have to trigger 3d_secure via Stripe.
                this.showFailed()
                break
              default:
            }
          }
        }
      } catch (err) {
        this.isSending = false
        console.log('err', err)
        this.orderErrors.push(err.message)
      }
    },
    handleSubmit() {
      this.$v.$touch()
      if (this.$v.$anyError) {
        return
      }
      this.pay()
    },
    checkCard(event) {
      this.complete = event.complete
    },
    async validateCoupon(value) {
      try {
        this.coupon = await this.loadCoupon(value)
      } catch (err) {
        console.log('Invalid coupon')
      }
    },
    showComplete() {
      let route = '/payment/confirmed'
      if (this.groupRedirect) {
        route = `${route}?redirect=group`
      } else if (this.forClient) {
        route = `${route}?redirect=messages`
      }
      this.$router.push(route)
    },
    showFailed() {
      this.$router.push('/payment/failure')
    },
    showBiometrics() {
      this.$router.push('/biometrics')
    },
    handleSkip() {
      if (this.bulk) {
        this.$router.push('/group/users')
      } if (this.$route.path === '/payment') {
        this.$router.push('/survey')
      } else {
        if (this.isMobile) {
          this.$router.push('/account')
        } else {
          this.$router.push('/user')
        }
      }
    }
  },
  async beforeCreate() {
    this.$store.commit('SET_STRIPE_PUBLISHABLE_KEY')
    this.$store.commit('SET_PAYMENT_REQUEST_FIELDSET')
  },
  async created() {
    await this.getMe()
    await this.loadPlans()
    this.form.name = this.getMyInfo.name
    this.form.email = this.getMyInfo.email
    this.form.phone = this.getMyInfo.phone
    this.states = await this.getStates()
  },
  mounted() {
    this.planList = this.getPlans
    this.selectedPlan = this.planList.find(p => p.nickname === 'Basic plan')
  },
  watch: {
    getPlans: function(newVal, oldVal) {
      if (newVal && newVal.length && !_.isEqual(newVal, oldVal)) {
        this.selectedPlan = _.cloneDeep(newVal[0])
        this.planList = []
        if (this.activeSub) {
          for (let plan of this.getPlans) {
            if (plan.nickname === 'Basic plan') {
              this.planList.push(plan)
              this.selectedPlan = plan
            }
          }
        } else {
          for (let plan of this.getPlans) {
            this.planList.push(plan)
          }
        }
      }
    }
  },
  validations: {
    form: {
      name: { required, isNameValid: helpers.regex('isNameValid', /^[a-z_ ]*$/i) },
      email: { required, email },
      address_line1: { required },
      address_line2: {},
      city: { required, isCityValid: helpers.regex('isCityValid', /^[a-z_ ]*$/i) },
      state: { required },
      zip: { required, numeric, isFiveDigits: (number) => number ? number.length === 5 : false },
      couponCode: {},
      phone: {}
    },
    planList: {},
    states: {},
    selectedPlan: { required },
    coupon: {}
  }
}
</script>



// WEBPACK FOOTER //
// src/app/components/Forms/PaymentForm.vue?523c6dae